import { useLoad } from "@redotech/react-util/load";
import { CustomerCustomFieldDataType } from "@redotech/redo-customer-segments-common/customer-custom-fields/customer-custom-fields-definition";
import { CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE } from "@redotech/redo-customer-segments-common/marketing/segments/segment-data-structures";
import { TimeframeConditionType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-timeframe";
import { CustomerCharacteristicType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import {
  ComparisonType,
  NumericCompareOperator,
  TokenCompareOperators,
} from "@redotech/redo-customer-segments-common/segments/segment-where-condition";
import { ExistsCompare } from "@redotech/redo-customer-segments-common/segments/segment-where-condition-zod-schema";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { SegmentBuilderText } from "../segment-builder-text";
import { CustomFieldConditionInProgress } from "../segment-in-progress";
import { BooleanFilter } from "./boolean-filter";
import { FullDateTimeframeFilter } from "./full-date-timeframe-filter";
import { NumericFilter } from "./numeric-filter";
import { PresenceOperators } from "./presence-operators";
import {
  normalizeTokenOptions,
  SegmentTokenKey,
  SegmentTokenOptionsContext,
  TokenFilter,
} from "./token-filter";

/** Every custom field comparison except the presence check. */
type ValueComparison = Exclude<
  CustomFieldConditionInProgress<any>["comparison"],
  ExistsCompare
>;

function comparisonForDataType(
  typeHint?: CustomerCustomFieldDataType,
): ValueComparison {
  switch (typeHint) {
    case CustomerCustomFieldDataType.STRING:
    case undefined:
      return {
        type: ComparisonType.TOKEN,
        operator: TokenCompareOperators.ANY,
        values: [],
      };
    case CustomerCustomFieldDataType.NUMBER:
      return {
        type: ComparisonType.NUMERIC,
        operator: NumericCompareOperator.EQ,
      };
    case CustomerCustomFieldDataType.BOOLEAN:
      return { type: ComparisonType.BOOLEAN, value: true };
    case CustomerCustomFieldDataType.DATE:
      return { type: TimeframeConditionType.BEFORE };
    default:
      assertNever(typeHint);
  }
}

/**
 * Custom field filter for editing custom field where conditions
 */
export const CustomFieldFilter = memo(function CustomFieldFilter({
  condition,
  setCondition,
}: {
  condition: CustomFieldConditionInProgress<any>;
  setCondition: (condition: CustomFieldConditionInProgress<any>) => void;
}) {
  const tokenOptionsFetcher = useContext(SegmentTokenOptionsContext);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);

  const customFieldsLoad = useLoad(
    async () =>
      tokenOptionsFetcher.getTokenOptions(
        new SegmentTokenKey({
          dataStructure: CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE,
          dataStructureField: CustomerCharacteristicType.CUSTOM_FIELDS,
        }),
        { search: debouncedSearch },
      ),
    [tokenOptionsFetcher, debouncedSearch],
  );

  const customFieldOptionsMatrix = useMemo(
    () => normalizeTokenOptions(customFieldsLoad.value).optionsMatrix,
    [customFieldsLoad.value],
  );

  const customFieldOptions: RedoListItem<string>[] = useMemo(() => {
    return (
      (customFieldOptionsMatrix ?? [])[0]?.map<RedoListItem<string>>((item) => {
        return { id: item.id, type: "text", value: item.id, text: item.text };
      }) ?? []
    );
  }, [customFieldOptionsMatrix]);

  const selectedCustomField: RedoListItem<string> | undefined = useMemo(() => {
    if (!condition.declarationId) {
      return undefined;
    }

    const matchingField = customFieldOptions.find(
      (option) => option.id === condition.declarationId,
    );

    if (matchingField) {
      return matchingField;
    }

    return {
      id: condition.declarationId,
      type: "text",
      value: condition.declarationId,
      text: condition.declarationId,
    };
  }, [condition.declarationId, customFieldOptions]);

  const selectedCustomFieldType = useMemo(
    () =>
      (customFieldOptionsMatrix ?? [])[0]?.find(
        (option) => option.id === condition.declarationId,
      )?.typeHint,
    [condition.declarationId, customFieldOptionsMatrix],
  );

  const handleSelectCustomField = (
    id: string,
    typeHint?: CustomerCustomFieldDataType,
  ) => {
    const newComparison = comparisonForDataType(typeHint);
    setCondition({
      ...condition,
      declarationId: id,
      comparison: newComparison,
    });
  };

  const presenceOperator =
    condition.comparison.type === ComparisonType.EXISTS
      ? condition.comparison.operator
      : undefined;

  const currentValueComparison: ValueComparison | undefined =
    condition.comparison.type === ComparisonType.EXISTS
      ? undefined
      : condition.comparison;

  /**
   * A presence check carries no value comparison, so the last one is kept to
   * decide which filter keeps rendering underneath it.
   */
  const [lastValueComparison, setLastValueComparison] = useState<
    ValueComparison | undefined
  >(currentValueComparison);

  const valueComparison =
    currentValueComparison ??
    lastValueComparison ??
    comparisonForDataType(selectedCustomFieldType);

  const presence: PresenceOperators = {
    operator: presenceOperator,
    setOperator: (operator) => {
      if (currentValueComparison) {
        setLastValueComparison(currentValueComparison);
      }
      setCondition({
        ...condition,
        comparison: { type: ComparisonType.EXISTS, operator },
      });
    },
  };

  const setValueComparison = (newComparison: ValueComparison) => {
    setLastValueComparison(newComparison);
    setCondition({ ...condition, comparison: newComparison });
  };

  const renderFilterComponent = () => {
    if (!condition.declarationId) {
      return null;
    }
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

    switch (valueComparison.type) {
      case ComparisonType.TOKEN:
        return (
          <TokenFilter
            comparison={valueComparison}
            dataStructure={CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE}
            dimension={CustomerCharacteristicType.CUSTOM_FIELDS}
            precedingValues={[condition.declarationId]}
            presence={presence}
            setComparison={setValueComparison}
          />
        );
      case ComparisonType.NUMERIC:
        return (
          <NumericFilter
            comparison={valueComparison}
            presence={presence}
            setComparison={setValueComparison}
          />
        );
      case ComparisonType.BOOLEAN:
        return (
          <BooleanFilter
            comparison={valueComparison}
            presence={presence}
            setComparison={setValueComparison}
          />
        );
      case undefined:
        return null;
      // Date custom fields use timeframe conditions (e.g. before, after, between)
      default:
        return (
          <FullDateTimeframeFilter
            comparison={valueComparison}
            presence={presence}
            setComparison={setValueComparison}
          />
        );
    }
  };

  return (
    <Flex align="center" gap="sm" wrap="wrap">
      <SegmentBuilderText>is</SegmentBuilderText>

      {/* Custom Field Declaration Selector */}
      <RedoSingleSelectDropdownInput
        fitToAnchor={false}
        flexGrow={false}
        itemsLoading={customFieldsLoad.pending}
        options={customFieldOptions}
        optionSelected={({ item }) => {
          if (item.value) {
            const typeHint = (customFieldOptionsMatrix ?? [])[0]?.find(
              (opt) => opt.id === item.value,
            )?.typeHint;
            handleSelectCustomField(item.value, typeHint);
          }
        }}
        placeholder="Select custom field"
        searchPlaceholder="Search"
        searchString={search}
        selectedItem={selectedCustomField}
        setSearchString={setSearch}
        size="xs"
      />
      {renderFilterComponent()}
    </Flex>
  );
});
